import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Rocket, Loader2, RotateCcw, BellRing } from "lucide-react";

const PHRASE = "OPEN BILLING";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA") : "—");

function Stat({ label, value, testid }) {
  return (
    <div className="pf-card p-5">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-2xl text-slate-900 mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}

function ReservationTable({ rows }) {
  return (
    <div className="pf-card overflow-hidden mt-6">
      <table className="w-full text-sm" data-testid="pre-launch-admin-table">
        <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
          <tr>
            <th className="px-5 py-3">Store</th><th className="px-5 py-3">Owner</th><th className="px-5 py-3">Plan</th>
            <th className="px-5 py-3">Provinces</th><th className="px-5 py-3">Reserved</th><th className="px-5 py-3">Notified</th><th className="px-5 py-3">Grace ends</th><th className="px-5 py-3">Reminders</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.store_id} className="border-t border-white/60" data-testid={`pre-launch-row-${r.store_id}`}>
              <td className="px-5 py-3 font-medium text-slate-900">{r.store_name}</td>
              <td className="px-5 py-3 text-xs text-slate-700">{r.owner_email}</td>
              <td className="px-5 py-3"><span className="pf-chip pf-chip-cyan">{(r.plan || "").toUpperCase()} · {r.cadence}</span></td>
              <td className="px-5 py-3 text-xs">{(r.provinces || []).join(", ")}</td>
              <td className="px-5 py-3 text-xs">{fmt(r.reserved_at)}</td>
              <td className="px-5 py-3 text-xs">{fmt(r.launch_notified_at)}</td>
              <td className="px-5 py-3 text-xs">{fmt(r.grace_ends_at)}</td>
              <td className="px-5 py-3 text-xs" data-testid={`pre-launch-row-reminders-${r.store_id}`}>
                {r.reminder_3d_at ? "3-day ✓" : "3-day —"} · {r.reminder_1d_at ? "1-day ✓" : "1-day —"}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-500">No reservations yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminPreLaunch() {
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get("/admin/pre-launch").then((r) => setData(r.data))
    .catch((e) => toast.error(e?.response?.data?.detail || "Could not load pre-launch state")), []);
  useEffect(() => { load(); }, [load]);

  const run = async (label, fn) => {
    try {
      setBusy(label);
      const { data: res } = await fn();
      if (label === "remind") toast.success(`Reminders sent · ${res.sent_3d} three-day · ${res.sent_1d} one-day${res.failed?.length ? ` · ${res.failed.length} failed` : ""}`);
      else if (label === "open") toast.success(`Billing opened · ${res.notified} merchant(s) emailed${res.failed?.length ? ` · ${res.failed.length} failed` : ""}`);
      else toast.success("Pre-launch mode restored");
      setConfirm("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed — super admin only");
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <div className="py-16 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div data-testid="admin-pre-launch">
      <div className="pf-chip pf-chip-lav"><Rocket className="w-3 h-3" /> Launch</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Pre-launch billing</h2>
      <p className="text-sm text-slate-600 mt-1">Merchants who reserved a plan before launch. Nobody is charged until billing is opened.</p>

      <div className="grid sm:grid-cols-4 gap-4 mt-6">
        <Stat label="Status" value={data.active ? "Pre-launch" : "Billing open"} testid="pre-launch-admin-status" />
        <Stat label="Reservations" value={data.reserved_count} testid="pre-launch-admin-reserved-count" />
        <Stat label="Notified" value={data.notified_count} testid="pre-launch-admin-notified-count" />
        <Stat label="Grace period" value={`${data.grace_days} days`} testid="pre-launch-admin-grace" />
      </div>
      {!data.env_flag && (
        <p className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="pre-launch-admin-env-off">
          PRE_LAUNCH_MODE env flag is off — subscribe already goes straight to Stripe.
        </p>
      )}

      <div className="pf-card p-6 mt-6" data-testid="pre-launch-admin-open-card">
        <div className="font-semibold text-slate-900">{data.active ? "Open billing (launch day)" : `Billing opened ${fmt(data.billing_opened_at)}`}</div>
        <p className="text-xs text-slate-600 mt-1">
          Ends pre-launch, emails every reserved merchant a link to complete checkout, and starts a {data.grace_days}-day grace period.
          Re-running only emails merchants not yet notified.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={`Type ${PHRASE}`}
                 className="pf-input px-3 py-2 text-sm w-56" data-testid="pre-launch-admin-confirm-input" />
          <button disabled={confirm.trim().toUpperCase() !== PHRASE || !!busy}
                  onClick={() => run("open", () => api.post("/admin/pre-launch/open-billing", { confirm }))}
                  className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="pre-launch-admin-open-btn">
            {busy === "open" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            {data.active ? "Open billing" : "Notify remaining"}
          </button>
          {!data.active && data.billing_opened_at && (
            <button disabled={!!busy} onClick={() => run("reset", () => api.post("/admin/pre-launch/reset"))}
                    className="pf-btn-glass px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                    data-testid="pre-launch-admin-reset-btn">
              <RotateCcw className="w-3.5 h-3.5" /> Return to pre-launch
            </button>
          )}
        </div>
      </div>

      {!data.active && (
        <div className="pf-card p-6 mt-6" data-testid="pre-launch-admin-reminders-card">
          <div className="font-semibold text-slate-900">Grace-period reminders</div>
          <p className="text-xs text-slate-600 mt-1">
            Emails merchants whose grace period ends within 3 days, and again within 1 day. Each reminder is sent only once per merchant.
          </p>
          <div className="mt-4 flex items-center gap-4">
            <button disabled={!!busy} onClick={() => run("remind", () => api.post("/admin/pre-launch/send-reminders"))}
                    className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="pre-launch-admin-remind-btn">
              {busy === "remind" ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
              Send reminders
            </button>
            <span className="text-xs text-slate-600" data-testid="pre-launch-admin-reminders-due">
              {data.reminders_due} due now
            </span>
          </div>
        </div>
      )}

      <ReservationTable rows={data.reservations || []} />
    </div>
  );
}
